// ===========================================================================
// Helpers da importação em lote (extraídos do BatchImportTab no rework 0060).
//
// Formatação (BRL, data, período, janela, tamanho de arquivo), leitura dos
// flags estruturados do preview (has_gps, existing_item — handoff 0060 §2) e
// classificação dos avisos verbatim que NÃO têm flag estruturada (janela
// ilegível, duplicado no arquivo). Os buckets validRows/warningRows/errorRows
// já são o veredito (§2.1) — não recomputamos classificação de bucket aqui.
// ===========================================================================

export const SCHEDULE_PERIOD_MORNING   = 'MANHA';
export const SCHEDULE_PERIOD_AFTERNOON = 'TARDE';

const WEEKDAYS_SHORT = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

// ---------------------------------------------------------------------------
// Datas (catálogo — Slice 2). Aritmética em UTC pra não driftar em fuso
// negativo; "hoje" é o dia LOCAL (o que o operador vê no relógio).
// ---------------------------------------------------------------------------
export function todayIsoLocal() {
   const now = new Date();
   const year = now.getFullYear();
   const month = String(now.getMonth() + 1).padStart(2, '0');
   const day = String(now.getDate()).padStart(2, '0');
   return `${year}-${month}-${day}`;
}

export function addDaysIso(isoDate, days) {
   const parts = String(isoDate).split('-').map(Number);
   if (parts.length !== 3 || parts.some(Number.isNaN)) return isoDate;
   const [year, month, day] = parts;
   const dt = new Date(Date.UTC(year, month - 1, day));
   dt.setUTCDate(dt.getUTCDate() + days);
   return dt.toISOString().slice(0, 10);
}

// ---------------------------------------------------------------------------
// Formatação
// ---------------------------------------------------------------------------
export function formatFileSize(bytes) {
   if (!Number.isFinite(bytes)) return '';
   const kilobytes = bytes / 1024;
   if (kilobytes < 1024) return `${kilobytes.toFixed(0)} KB`;
   return `${(kilobytes / 1024).toFixed(2)} MB`;
}

export function formatBrlCurrency(value) {
   if (value === null || value === undefined) return '—';
   try {
      return Number(value).toLocaleString('pt-BR', {
         style: 'currency',
         currency: 'BRL',
      });
   } catch {
      return String(value);
   }
}

// `YYYY-MM-DD` (input nativo) → `DD/MM/YYYY`. Split manual evita shift de fuso
// ao instanciar Date a partir de string ISO sem timezone.
export function formatScheduleDateBr(isoDate) {
   if (!isoDate || typeof isoDate !== 'string') return '';
   const parts = isoDate.split('-');
   if (parts.length !== 3) return isoDate;
   const [year, month, day] = parts;
   return `${day}/${month}/${year}`;
}

// `YYYY-MM-DD` → `DD/MM` (chips compactos do card/modal).
export function formatDayMonthBr(isoDate) {
   if (!isoDate || typeof isoDate !== 'string') return '';
   const parts = isoDate.split('-');
   if (parts.length !== 3) return isoDate;
   const [, month, day] = parts;
   return `${day}/${month}`;
}

// `YYYY-MM-DD` → 'Seg'/'Ter'… Usa Date.UTC (não o construtor local) pra o
// dia-da-semana não driftar em fuso negativo.
export function formatWeekdayShortBr(isoDate) {
   if (!isoDate || typeof isoDate !== 'string') return '';
   const parts = isoDate.split('-').map(Number);
   if (parts.length !== 3 || parts.some(Number.isNaN)) return '';
   const [year, month, day] = parts;
   return WEEKDAYS_SHORT[new Date(Date.UTC(year, month - 1, day)).getUTCDay()] ?? '';
}

export function formatSchedulePeriodLabel(period) {
   if (period === SCHEDULE_PERIOD_MORNING)   return 'Manhã';
   if (period === SCHEDULE_PERIOD_AFTERNOON) return 'Tarde';
   return period ?? '';
}

// Janela de atendimento "HH:MM–HH:MM" (handoff 0049): horário marcado do
// cliente. `HH:MM:SS` (ou prefixo) → `HH:MM`; sem janela → null.
export function formatImportWindow(start, end) {
   const trim = (t) => (t ? String(t).slice(0, 5) : null);
   const s = trim(start);
   const e = trim(end);
   if (!s && !e) return null;
   return `${s || '…'}–${e || '…'}`;
}

// Iniciais (até 2 palavras) para o avatar do card. Vazio → '?'.
export function initials(name) {
   const words = String(name || '')
      .trim()
      .split(/\s+/)
      .filter(Boolean);
   if (words.length === 0) return '?';
   const first = words[0][0] ?? '';
   const second = words.length > 1 ? words[words.length - 1][0] ?? '' : '';
   return `${first}${second}`.toUpperCase();
}

// Rótulo da base pelo código (clusterOrigins vem do orquestrador).
export function baseLabel(clusterOrigins, code) {
   if (!code) return '';
   const found = (clusterOrigins || []).find((b) => b.origin_code === code);
   return found?.origin_label || code;
}

// URL da listagem filtrada pós-confirm (mesmo dia/período do lote).
export function buildListingRedirectUrl(scheduleDate, schedulePeriod) {
   const params = new URLSearchParams({
      date_from:       scheduleDate,
      date_to:         scheduleDate,
      schedule_period: schedulePeriod,
   });
   return `/routing/items?${params.toString()}`;
}

// ---------------------------------------------------------------------------
// Leitura dos flags do preview (handoff 0060 §2)
// ---------------------------------------------------------------------------
// GPS resolvido. Prefere o flag estruturado has_gps; cai na coordenada crua
// se o backend ainda não emitir o flag (defensivo).
export function rowHasGps(row) {
   if (typeof row?.has_gps === 'boolean') return row.has_gps;
   return (
      row?.address_latitude !== null &&
      row?.address_latitude !== undefined &&
      row?.address_longitude !== null &&
      row?.address_longitude !== undefined
   );
}

// `existing_item` ({ id, item_status } | null): pedido já presente na
// (data, período). null = não existe.
export function rowExistingItem(row) {
   return row?.existing_item ?? null;
}

// Duplicata restaurável: o ocupante da chave é um CANCELADO → o card pode
// oferecer "Restaurar item #id" (handoff 0059/0060).
export function rowIsRestorableCancelled(row) {
   return rowExistingItem(row)?.item_status === 'CANCELADO';
}

// Classifica um aviso verbatim que NÃO tem flag estruturada (handoff 0031 §2):
//   dupfile → "Pedido X duplicado neste arquivo (linha N e M)"
//   window  → "Janela de horário … não reconhecida"
// GPS e "já existe" saem dos flags (rowHasGps / rowExistingItem), não daqui.
export function classifyWarning(warning) {
   const w = String(warning || '').toLowerCase();
   if (w.includes('duplicado neste arquivo')) return 'dupfile';
   if (w.includes('janela de horário') || w.includes('janela de horario')) {
      return 'window';
   }
   if (w.includes('já existe') || w.includes('ja existe')) return 'duplicate';
   if (w.includes('coordenada')) return 'nocoord';
   return 'other';
}

// Duplicata (ativa OU cancelada) — começa DESMARCADA por padrão (o confirm dela
// daria 409). Prefere o flag; cai no texto do aviso p/ backend legado.
export function rowIsDuplicate(row) {
   if (rowExistingItem(row) != null) return true;
   return (row?.warnings || []).some((w) => classifyWarning(w) === 'duplicate');
}
